import { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { notifyError } from '../components/common/toast/ToastFunc';
import EventEmitter from 'events';

const AuthContext = createContext();
const authEventEmitter = new EventEmitter();

export const triggerLogoutEvent = (path = "/") => {
  authEventEmitter.emit('logout', path);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState({ role: "", first_name: "" });
  const [token, setToken] = useState(null);
  const [isAppRendered, setIsAppRendered] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const getUserData = useCallback(async (token) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/customer/user-info/get-data`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const responseData = response.data?.data;
      return responseData;
    } catch (error) {
      throw new Error("Something went wrong.");
    }
  }, []);

  const login = useCallback(async (token, expirationDate) => {
    try {
      const { role, first_name } = await getUserData(token);
      setToken(token);
      setUser({
        "role": role,
        "first_name": first_name
      })
      const tokenExpirationTime = expirationDate || new Date(new Date().getTime() + 1000 * 60 * 15);
      sessionStorage.setItem(
        'userData',
        JSON.stringify({
          user: {
            role,
            first_name
          },
          token,
          expiration: tokenExpirationTime.toISOString()
        })
      );
      navigate("/banking");
    } catch (error) {
      notifyError(error.message || "Something went wrong");;
    }
  }, [getUserData, navigate]);

  const logout = useCallback((path = '') => {
    try {
      setToken(null);
      setUser({});
      sessionStorage.removeItem('userData');
      navigate(path);
      setIsAppRendered(true);
    }
    catch (error) {
      notifyError("Something went wrong");
    }
  }, [navigate]);

  const validateSessionData = useCallback(async (sessionData) => {
    let responseData;
    try {
      responseData = await getUserData(sessionData.token);
      if (responseData && responseData?.role === sessionData.user?.role && responseData?.first_name === sessionData.user?.first_name) {
        setToken(sessionData.token);
        setUser({
          "role": sessionData.user?.role,
          "first_name": sessionData.user?.first_name
        })
        navigate(location.pathname);
        setIsAppRendered(true);
      }
      else {
        logout();
      }
    } catch (error) {
      logout();
    }
  }, [getUserData, location.pathname, logout, navigate]);

  useEffect(() => {
    try {
      authEventEmitter.on('logout', logout);
      const sessionData = JSON.parse(sessionStorage.getItem('userData'))
      if (sessionData && sessionData.user && sessionData.token && new Date(sessionData.expiration) > new Date()) {
        validateSessionData(sessionData);
      }
      else {
        logout(location.pathname);
      }
      return () => {
        authEventEmitter.removeListener('logout', logout); // Clean up listener
      };
    }
    catch (error) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAppRendered }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;